import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, OnInit, OnDestroy, OnChanges, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { takeUntil, finalize } from "rxjs/operators";
import { addDays, parseISO } from "date-fns";
import { IDashboardWidget } from "src/app/models/dashboard";
import { SampleService } from "src/app/services/sample.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { WidgetOrgFilterComponent, ORG_LEVEL_OVERRIDE_KEY } from "src/app/common-components/widget-org-filter/widget-org-filter.component";
import { Sample } from "src/app/models/sample.model";
import { IhLabResult } from "src/app/models/IhLabResult.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { DATE_FILTER_TYPE_ENUM } from "src/app/shared/grid/baseGrid.component";
import { SAMPLE_GRID_ROUTE } from "src/app/constants/dashboard.constants";
import { SampleCategoryEnum } from "src/app/enums/sampleCategory.enum";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import {
    ExceedanceComplianceRequest,
    ExceedanceComplianceResponse,
    ExceedanceByAgent,
    ExceedanceTimePeriod,
    ExceedingLabResultFilter,
    EXCEEDING_LAB_RESULT_FILTER_KEY,
} from "src/app/models/exceedance-compliance.model";
import { ExceedanceComplianceFiltersComponent } from "./exceedance-compliance-filters.component";
import { TrendIconPipe } from "src/app/shared/pipes/trend-icon.pipe";
import {
    ExceedanceComplianceFilterValues,
    ExceedanceSortColumn,
    EXCEEDANCE_FILTER_DEFAULTS,
    EXCEEDANCE_TABLE_HEADERS,
    DEFAULT_EXCEEDANCE_SORT_COLUMN,
    DEFAULT_EXCEEDANCE_SORT_ASC,
    configToFilterValues,
    filterValuesToConfig,
    SAMPLE_FILTER_KEYS,
    APPROVED_SAMPLE_STATUSES,
    toDisplayRange,
    toUnixSeconds,
} from "./exceedance-compliance-widget.constants";
import { BaseDashboardWidget } from "src/app/components/dashboard/dashboard-widgets/_shared/base-dashboard-widget";
import { WidgetTableSort } from "src/app/components/dashboard/dashboard-widgets/_shared/widget-table-sort";

const IH_LAB_RESULTS_KEY = nameOf<Sample>((x) => x.ihLabResults);

interface ExceedanceKpiVm {
    trendClass: string;
    trendLabel: string;
    showTrendIcon: boolean;
}

interface ExceedanceAgentVm extends ExceedanceByAgent {
    badgeClass: string;
    trendClass: string;
    trendDisplay: string;
}

interface ExceedanceComplianceVm {
    complianceRate: ExceedanceKpiVm;
    isComplianceRateEvaluable: boolean;
    totalExceedances: ExceedanceKpiVm;
    totalSamples: ExceedanceKpiVm;
    segsWithExceedances: ExceedanceKpiVm;
    agents: ExceedanceAgentVm[];
    resolvedDateRange: string;
    resolvedPriorDateRange: string;
}

@Component({
    standalone: true,
    selector: "app-exceedance-compliance-widget",
    imports: [
        CommonModule,
        TranslateModule,
        MatTooltipModule,
        ExceedanceComplianceFiltersComponent,
        TrendIconPipe,
        WidgetOrgFilterComponent,
    ],
    templateUrl: "./exceedance-compliance-widget.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./exceedance-compliance-widget.component.scss"]
})
export class ExceedanceComplianceWidgetComponent
    extends BaseDashboardWidget
    implements OnInit, OnDestroy, OnChanges, IDashboardWidget
{
    data?: ExceedanceComplianceResponse;
    vm?: ExceedanceComplianceVm;
    sortedAgents: ExceedanceAgentVm[] = [];
    filterValues: ExceedanceComplianceFilterValues = {
        ...EXCEEDANCE_FILTER_DEFAULTS,
    };

    readonly tableHeaders = EXCEEDANCE_TABLE_HEADERS;
    readonly sort = new WidgetTableSort<ExceedanceAgentVm, ExceedanceSortColumn>(
        (row, column) => row[column],
        DEFAULT_EXCEEDANCE_SORT_COLUMN,
        DEFAULT_EXCEEDANCE_SORT_ASC,
    );

    private requestVersion = 0;

    constructor(
        private sampleService: SampleService,
        private translate: TranslateService,
        private router: Router,
        private dateFormatService: DateFormatService,
    ) {
        super();
    }

    get effectiveOrgIdForFilters(): string | null {
        return this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig) ?? null;
    }

    get isSiteSelectorDisabled(): boolean {
        return this.filterValues.selectedJurisdictionId != null;
    }

    onFilterChange(values: ExceedanceComplianceFilterValues): void {
        this.filterValues = values;
        const orgOverride = this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY];
        this.widgetConfig = orgOverride != null
            ? { ...filterValuesToConfig(values), [ORG_LEVEL_OVERRIDE_KEY]: orgOverride }
            : filterValuesToConfig(values);
        this.configChange.emit(this.widgetConfig);
        this.reload();
    }

    onConfigChange?(config: Record<string, unknown>): void {
        this.widgetConfig = config;
        this.syncFromConfig();
        this.reload();
    }

    onAgentRowClick(agent: ExceedanceByAgent): void {
        const effectiveOrgId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
        const filters = this.buildExceedanceFilters(this.filterValues, effectiveOrgId, agent.agentId);
        const queryParams = { additionalFilters: JSON.stringify(filters) };
        this.router.navigate([SAMPLE_GRID_ROUTE], { queryParams });
    }

    viewFullReport(): void {
        const effectiveOrgId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
        const filters = this.buildExceedanceFilters(this.filterValues, effectiveOrgId);
        const queryParams = { additionalFilters: JSON.stringify(filters) };
        this.router.navigate([SAMPLE_GRID_ROUTE], { queryParams });
    }

    sortBy(column: ExceedanceSortColumn): void {
        this.sort.toggle(column);
        this.sortedAgents = this.sort.apply(this.sortedAgents);
    }

    protected override syncFromConfig(): void {
        this.filterValues = configToFilterValues(this.widgetConfig);
    }

    protected override reload(): void {
        this.loadData();
    }

    private buildExceedanceFilters(
        filterValues: ExceedanceComplianceFilterValues,
        effectiveOrgId?: string,
        rowAgentId?: string,
    ): Record<string, string> {
        const filters: Record<string, string> = {};

        if (effectiveOrgId != null) {
            filters[nameOf<ExceedanceComplianceRequest>(x => x.organizationLevelId)] = effectiveOrgId.toString();
        }

        if (this.data) {
            filters[DATE_FILTER_TYPE_ENUM] = JSON.stringify({
                dateFilterType: DateFilterTypeEnum.Custom,
                startDate: toUnixSeconds(this.data.resolvedStartDate),
                endDate: toUnixSeconds(this.data.resolvedEndDate),
                previousStartDate: 0,
                previousEndDate: 0,
            });
        }

        if (filterValues.selectedOelTypeId != null) {
            filters[`${IH_LAB_RESULTS_KEY}.${nameOf<IhLabResult>((x) => x.oelTypeId)}`] = filterValues.selectedOelTypeId.toString();
        }

        const agentIds = rowAgentId ? [rowAgentId] : filterValues.selectedAgentIds;
        if (agentIds.length) {
            filters[`${IH_LAB_RESULTS_KEY}.${nameOf<IhLabResult>((x) => x.agentId)}`] = agentIds.join(",");
        }

        filters[EXCEEDING_LAB_RESULT_FILTER_KEY] = JSON.stringify(this.buildExceedingLabResultFilter(filterValues, agentIds));

        if (filterValues.selectedJurisdictionId != null) {
            filters[`${IH_LAB_RESULTS_KEY}.${nameOf<IhLabResult>((x) => x.jurisdictionId)}`] = filterValues.selectedJurisdictionId.toString();
        }

        if (filterValues.selectedSampleCategoryId != null && filterValues.selectedSampleCategoryId !== SampleCategoryEnum.Blank) {
            filters[SAMPLE_FILTER_KEYS.sampleCategory] = filterValues.selectedSampleCategoryId.toString();
        }

        if (filterValues.selectedSampleTypeId != null) {
            filters[SAMPLE_FILTER_KEYS.sampleType] = filterValues.selectedSampleTypeId.toString();
        }

        if (filterValues.selectedSurveyTypeIds.length) {
            filters[SAMPLE_FILTER_KEYS.surveyTypeId] = filterValues.selectedSurveyTypeIds.join(",");
        }

        filters[SAMPLE_FILTER_KEYS.sampleStatus] = APPROVED_SAMPLE_STATUSES;

        return filters;
    }

    private buildExceedingLabResultFilter(
        filterValues: ExceedanceComplianceFilterValues,
        agentIds: string[],
    ): ExceedingLabResultFilter {
        const filter: ExceedingLabResultFilter = {};
        if (agentIds.length) {
            filter.agentIds = agentIds;
        }
        if (filterValues.selectedAgentGroupIds.length) {
            filter.agentGroupIds = filterValues.selectedAgentGroupIds;
        }
        if (filterValues.selectedOelTypeId != null) {
            filter.oelTypeId = filterValues.selectedOelTypeId;
        }
        if (filterValues.selectedJurisdictionId != null) {
            filter.jurisdictionId = filterValues.selectedJurisdictionId;
        }
        return filter;
    }

    private customRangeSeconds(filterValues: ExceedanceComplianceFilterValues): { start: number; end: number } | null {
        if (filterValues.selectedTimePeriod !== ExceedanceTimePeriod.Custom || !filterValues.customStartDate || !filterValues.customEndDate) {
            return null;
        }
        return {
            start: this.dateFormatService.convertDateToTimestamp(parseISO(filterValues.customStartDate)),
            end: this.dateFormatService.convertDateToTimestamp(addDays(parseISO(filterValues.customEndDate), 1)),
        };
    }

    getExceedanceRateBadgeClass(rate: number): string {
        if (rate > 10) return "badge-red";
        if (rate >= 5) return "badge-orange";
        if (rate >= 2) return "badge-yellow";
        return "badge-green";
    }

    getTrendIcon(trend: number | null): string {
        if (trend == null || trend === 0) return "remove";
        return trend > 0 ? "arrow_upward" : "arrow_downward";
    }

    getTrendClass(trend: number | null, invertColors = false): string {
        if (trend == null || trend === 0) return "trend-neutral";
        const isPositive = trend > 0;
        if (invertColors)
            return isPositive ? "trend-negative" : "trend-positive";
        return isPositive ? "trend-positive" : "trend-negative";
    }

    formatTrend(trend: number | null, isPercent = false): string {
        if (trend == null) return "";
        const sign = trend > 0 ? "+" : "";
        return isPercent ? `${sign}${trend}%` : `${sign}${trend}`;
    }

    formatTrendLabel(trend: number | null, priorDateRange = '', isPercent = false): string {
        if (trend == null) return "";
        const formatted = this.formatTrend(trend, isPercent);
        const suffix = priorDateRange ? `${this.translate.instant("vs prior")} (${priorDateRange})` : this.translate.instant("vs prior period");
        return `${formatted} ${suffix}`;
    }

    trackByAgentId(_index: number, agent: ExceedanceAgentVm): string {
        return agent.agentId;
    }

    private buildKpiVm(trend: number | null, invertColors = false, isPercent = false, priorDateRange = ''): ExceedanceKpiVm {
        return {
            trendClass: this.getTrendClass(trend, invertColors),
            trendLabel: this.formatTrendLabel(trend, priorDateRange, isPercent),
            showTrendIcon: trend != null && trend !== 0,
        };
    }

    private formatDateRangeLabel(start: string, end: string): string {
        if (!start || !end) return '';
        const range = toDisplayRange(
            { startSeconds: toUnixSeconds(start), endSeconds: toUnixSeconds(end) },
            seconds => this.dateFormatService.convertTimestampToDate(seconds),
        );
        const fmt = (d: Date) => this.dateFormatService.formatDateTimeToString(d, 'MMM d, yyyy');
        return `${fmt(range.start)} – ${fmt(range.end)}`;
    }

    private buildVm(response: ExceedanceComplianceResponse): ExceedanceComplianceVm {
        const priorDateRange = this.formatDateRangeLabel(response.resolvedPriorStartDate, response.resolvedPriorEndDate);
        return {
            complianceRate: this.buildKpiVm(response.complianceRateTrend, false, true, priorDateRange),
            isComplianceRateEvaluable: response.isComplianceRateEvaluable,
            totalExceedances: this.buildKpiVm(response.totalExceedancesTrend, true, false, priorDateRange),
            totalSamples: this.buildKpiVm(response.totalSamplesTrend, false, false, priorDateRange),
            segsWithExceedances: this.buildKpiVm(response.segsWithExceedancesTrend, true, false, priorDateRange),
            agents: response.exceedancesByAgent.map(agent => ({
                ...agent,
                badgeClass: this.getExceedanceRateBadgeClass(agent.exceedanceRate),
                trendClass: this.getTrendClass(agent.exceedanceRateTrend, true),
                trendDisplay: this.formatTrend(agent.exceedanceRateTrend, true),
            })),
            resolvedDateRange: this.formatDateRangeLabel(response.resolvedStartDate, response.resolvedEndDate),
            resolvedPriorDateRange: priorDateRange,
        };
    }

    private loadData(): void {
        const currentVersion = ++this.requestVersion;
        this.isLoading = true;
        this.error = undefined;

        const f = this.filterValues;
        const effectiveOrgId = this.resolveEffectiveOrgIdAndRecord();
        const timePeriod = f.selectedTimePeriod ?? ExceedanceTimePeriod.Last12Months;
        const customRange = this.customRangeSeconds(f);

        const request: ExceedanceComplianceRequest = {
            timePeriod,
            oelTypeId: f.selectedOelTypeId ?? undefined,
            agentIds: f.selectedAgentIds.length ? f.selectedAgentIds : undefined,
            agentGroupIds: f.selectedAgentGroupIds.length ? f.selectedAgentGroupIds : undefined,
            surveyTypeIds: f.selectedSurveyTypeIds.length ? f.selectedSurveyTypeIds : undefined,
            startDate: customRange?.start,
            endDate: customRange?.end,
            organizationLevelId: effectiveOrgId,
            jurisdictionId: f.selectedJurisdictionId ?? undefined,
            sampleCategory: f.selectedSampleCategoryId ?? undefined,
            sampleType: f.selectedSampleTypeId ?? undefined,
        };

        this.sampleService
            .getExceedanceCompliance(request)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    if (currentVersion === this.requestVersion) {
                        this.isLoading = false;
                    }
                }),
            )
            .subscribe({
                next: (response: ExceedanceComplianceResponse) => {
                    if (currentVersion !== this.requestVersion) return;
                    this.data = response;
                    this.vm = this.buildVm(response);
                    this.sortedAgents = this.sort.apply(this.vm.agents);
                },
                error: () => {
                    if (currentVersion !== this.requestVersion) return;
                    this.error = this.translate.instant(
                        "Failed to load exceedance compliance data",
                    );
                    this.data = undefined;
                    this.sortedAgents = [];
                },
            });
    }

    public trackByIndex = trackByIndex;
}
